/**
 * Truy cập dữ liệu màn "Tài khoản của tôi" (ADR-0027, ADR-0028).
 *
 * Nguồn thật là **Account REST API của Keycloak** (`lib/api/keycloak-account.ts`),
 * gọi bằng access token của chính người dùng — giao diện không gọi Admin API.
 * Đổi mật khẩu, cài và gỡ OTP **không** đi qua API mà qua application-initiated
 * action: trình duyệt sang trang Keycloak, làm đúng một bước, rồi quay về —
 * tạo mã khôi phục cũng vậy (`CONFIGURE_RECOVERY_AUTHN_CODES`); trạng thái đã
 * tạo hay chưa đọc từ cùng `GET /credentials` (loại `recovery-authn-codes`).
 *
 * ADR-0027 bước 1 chỉ tắt `account-console`; client `account` và role
 * `manage-account` còn giữ cho các lời gọi ở đây. Khi tắt nốt client `account`,
 * `phien()` và `dangXuatPhien()` chuyển sang user-service; `xacThuc()` cần một
 * nguồn mới cho danh sách OTP. Chỉ sửa tệp này.
 */
import { goiAccountApi } from '../api/keycloak-account';
import { dungApiThat } from '../api/http';
import { batBuocMfaTheoToken, layKetQuaHanhDong, moHanhDongKeycloak, oidcEnabled } from '../auth/oidc';
import type { KetQuaHanhDong } from '../auth/oidc';
import { MOCK_PHIEN, MOCK_XAC_THUC, MOCK_XAC_THUC_RONG } from '../../mock/tai-khoan';
import { mockRequest } from '../../mock/transport';
import { docMaKhoiPhuc } from '../../modules/tai-khoan/nghiep-vu/ma-khoi-phuc';
import type { NhomCredential } from '../../modules/tai-khoan/nghiep-vu/ma-khoi-phuc';
import type {
  HanhDongKeycloak,
  PhienDangNhap,
  UngDungXacThuc,
  XacThucCuaToi,
} from '../../modules/tai-khoan/types';


/** Một thiết bị của `GET /account/sessions/devices`. */
interface ThietBi {
  os?: string;
  osVersion?: string;
  sessions?: {
    id: string;
    ipAddress?: string;
    started?: number;
    lastAccess?: number;
    browser?: string;
    current?: boolean;
    clients?: { clientId: string; clientName?: string }[];
  }[];
}

/** Account REST API: `createdDate` tính bằng mili giây, phiên tính bằng giây. */
const tuMiliGiay = (ms?: number): string | null => (ms ? new Date(ms).toISOString() : null);
const tuGiay = (s?: number): string => new Date((s ?? 0) * 1000).toISOString();

function docXacThuc(nhom: NhomCredential[]): XacThucCuaToi {
  const cua = (loai: string) => nhom.find((n) => n.type === loai)?.userCredentialMetadatas ?? [];
  const ungDungXacThuc: UngDungXacThuc[] = cua('otp').map(({ credential: c }) => ({
    id: c.id,
    nhan: c.userLabel?.trim() || null,
    taoLuc: tuMiliGiay(c.createdDate),
  }));
  return {
    ungDungXacThuc,
    maKhoiPhuc: docMaKhoiPhuc(nhom),
    batBuocMfa: batBuocMfaTheoToken(),
    matKhauDatLuc: tuMiliGiay(cua('password')[0]?.credential.createdDate),
  };
}

function docPhien(thietBi: ThietBi[]): PhienDangNhap[] {
  return thietBi
    .flatMap((tb) =>
      (tb.sessions ?? []).map((p) => ({
        id: p.id,
        trinhDuyet: p.browser ?? '',
        heDieuHanh: [tb.os, tb.osVersion && tb.osVersion !== 'Unknown' ? tb.osVersion : '']
          .filter(Boolean)
          .join(' '),
        ip: p.ipAddress ?? '',
        batDauLuc: tuGiay(p.started),
        truyCapCuoiLuc: tuGiay(p.lastAccess),
        ungDung: (p.clients ?? []).map((c) => c.clientName || c.clientId),
        hienTai: Boolean(p.current),
      })),
    )
    // Phiên đang xem lên đầu, còn lại mới truy cập trước.
    .sort(
      (a, b) =>
        Number(b.hienTai) - Number(a.hienTai) || b.truyCapCuoiLuc.localeCompare(a.truyCapCuoiLuc),
    );
}

/** Bản sao trong phiên: đăng xuất một phiên mẫu thì danh sách thấy ngay. */
let phienMau: PhienDangNhap[] = [...MOCK_PHIEN];

export const taiKhoanRepository = {
  xacThuc: async (): Promise<XacThucCuaToi> => {
    if (!dungApiThat()) return mockRequest(MOCK_XAC_THUC, MOCK_XAC_THUC_RONG);
    return docXacThuc(await goiAccountApi<NhomCredential[]>('/credentials'));
  },

  phien: async (): Promise<PhienDangNhap[]> => {
    if (!dungApiThat()) return mockRequest(phienMau, []);
    return docPhien(await goiAccountApi<ThietBi[]>('/sessions/devices'));
  },

  dangXuatPhien: async (id: string): Promise<void> => {
    if (!dungApiThat()) {
      phienMau = phienMau.filter((p) => p.id !== id);
      return mockRequest(undefined);
    }
    await goiAccountApi<void>(`/sessions/${encodeURIComponent(id)}`, 'DELETE');
  },

  /** Đăng xuất mọi phiên **trừ** phiên đang xem (`current=false`). */
  dangXuatPhienKhac: async (): Promise<void> => {
    if (!dungApiThat()) {
      phienMau = phienMau.filter((p) => p.hienTai);
      return mockRequest(undefined);
    }
    await goiAccountApi<void>('/sessions?current=false', 'DELETE');
  },

  /**
   * Có Keycloak để mở action hay không. Chế độ dữ liệu mẫu (không có
   * `/config.json`) thì không có trang nào để chuyển sang.
   */
  moDuocHanhDong: (): boolean => oidcEnabled(),

  moHanhDong: (hanhDong: HanhDongKeycloak, quayVe: string): Promise<void> =>
    moHanhDongKeycloak(hanhDong, quayVe),

  /** Kết quả action vừa quay về, đọc một lần. */
  ketQuaHanhDong: (): KetQuaHanhDong | null => layKetQuaHanhDong(),
};
